import { MouseEventHandler, useMemo, useRef } from "react";
import { useLocSettings } from "../hooks/SettingsHooks";
import classNames from "classnames";
import { encodeFilterQstring } from "../utils/ProductsUtils";
import { useMediaQuery } from "react-responsive";
import {
  useBjelinTranslations,
  useSpecsTranslation,
  useUnitTranslation
} from "../hooks/TranslationHooks";
import COLORS from "../config/colors";
import { useTooltip } from "./ToolTip";
import { useNavigate } from "react-router-dom";

const TooltipContent = ({
  field,
  value,
  unit,
  valueDescription,
  clickToFilter = false,
  filterText
}: {
  field: string;
  value: string;
  unit?: string;
  valueDescription?: string;
  clickToFilter?: boolean;
  filterText?: string;
}) => {
  return (
    <div key="specTooltip">
      <div>
        <h4>
          {field}: {value} {unit}
        </h4>
        <div dangerouslySetInnerHTML={{ __html: valueDescription ?? "" }} />
        {clickToFilter && <div>{filterText}</div>}
      </div>
    </div>
  );
};

const IconWithPopup = ({
  field,
  value,
  unit,
  color,
  showLabel = false,
  sku,
  productCategoryUrl = "",
  noTooltip = false,
  clickToFilter = false
}: {
  field: string;
  value: string;
  unit?: string;
  color?: string;
  showLabel?: boolean;
  sku?: string;
  productCategoryUrl?: string;
  noTooltip?: boolean;
  clickToFilter?: boolean;
}) => {
  const settings = useLocSettings();
  const navigate = useNavigate();
  const hasHover = useMediaQuery({ query: "(hover: hover)" });
  const t = useBjelinTranslations();
  const ts = useSpecsTranslation();
  const tu = useUnitTranslation();
  const tooltipParent = useRef<HTMLSpanElement>(null);

  const icon = settings.specifications?.[field]?.icons?.[value];
  const valueDescription =
    settings.specifications?.[field]?.value_descriptions?.[value]?.description;
  const description = settings.specifications?.[field]?.description;
  const id =
    "icon_" +
    sku +
    "_" +
    (showLabel ? "l" : "n") +
    "_" +
    field.replace(/[^a-zA-Z0-9]/g, "") +
    "_" +
    value.replace(/[^a-zA-Z0-9]/g, "");

  const tooltipContent = useMemo(
    () =>
      noTooltip ? null : (
        <TooltipContent
          field={ts(field)}
          value={ts(field, value)}
          unit={unit && tu(unit)}
          valueDescription={valueDescription}
          clickToFilter={clickToFilter}
          filterText={t("click.to.filter")}
        />
      ),
    [
      noTooltip,
      ts,
      field,
      value,
      unit,
      tu,
      valueDescription,
      description,
      clickToFilter,
      t
    ]
  );

  useTooltip(tooltipParent, tooltipContent);

  const filterClick: MouseEventHandler = e => {
    if (!noTooltip) {
      e.preventDefault();
      e.stopPropagation();
      if (hasHover && clickToFilter) {
        navigate(
          productCategoryUrl +
            "?" +
            new URLSearchParams({
              filter: encodeFilterQstring({ [field]: [value] })
            })
        );
      }
    }
  };

  if (icon) {
    return (
      <>
        <span
          className={classNames("spec-wrapper", { "with-label": showLabel })}
        >
          <span
            ref={tooltipParent}
            className={"spec-icon"}
            style={{
              color: color ? color : icon.color || COLORS.bjelin_grey,
              ...((clickToFilter || (!!description && !noTooltip)) && {
                cursor: "pointer"
              })
            }}
            dangerouslySetInnerHTML={{
              __html: color
                ? (icon.svg2 || icon.svg)?.replaceAll(
                    /fill="#[0-9a-fA-F]{3,6}"/g,
                    'fill="currentColor"'
                  )
                : icon.svg2 || icon.svg
            }}
            id={id}
            onClick={filterClick}
          />
          {showLabel && (
            <span className={"spec-icon-label"}>
              {ts(field, value)} {unit && tu(unit)}
            </span>
          )}
        </span>
      </>
    );
  }
  return null;
};

export default IconWithPopup;
